import React, { useCallback, useEffect, useMemo, useState } from "react"
import { loadStripe, StripeElementsOptions, StripePaymentElementOptions, StripeAddressElementOptions } from "@stripe/stripe-js"
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js"
import { GetApp } from "../App"
import { iapPurchaseEnabled, subscriptionExpired } from "../App/IAP"
import { Native } from "../App/Native"
import type { AppStoreSyncResult } from "../App/Native"
import { classNames } from "../Components"
import { Block, BlockContainer, BlockField, BlockRow } from "../Components/Block"
import { ConfirmDialog } from "../Components/ConfirmDialog"
import { InfoDialog } from "../Components/InfoDialog"
import { Loading } from "../Components/Loading"
import { LocalizedMessage } from "../Components/LocalizedMessage"
import { IAccount, IAccountSubscriptionStatus, IAccountPaymentSystem, IAccountSubscriptionPlan, IAccountSubscriptionPlanPricing, IAccountSubscriptionPlansResponse, ApiClient, AccountApi, Stripe } from "../Types"
import { logger } from "../App/Logger"
import { AccountViewCurrency } from "./Account/Params"
import { AccountViewPlanBlock } from "./Account/PlanBlock"
import { AccountViewPlanBlockNonStripe } from "./Account/PlanBlockNonStripe"
import { useAccountPlanCatalog } from "./Account/useAccountPlanCatalog"

export const AccountViewData = {
  completeCheckout(api: ApiClient, sessionId: string) {
    return new Stripe(api).account.checkoutComplete({ params: { sessionId } }).call();
  },
  loadPlans(api: ApiClient): Promise<IAccountSubscriptionPlansResponse> {
    return new AccountApi(api).account.plans({ params: {} }).call();
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  checkout(api: ApiClient, values: any) {
    return new Stripe(api).account.checkout({ params: {}, body: values }).call();
  },
  cancelPlan(api: ApiClient) {
    return new Stripe(api).account.updatePlan({ params: {} }).call();
  },
};

interface AccountViewProps {
  account: IAccount;
  checkoutSessionId?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  backAction: any;
}

interface AccountSubscriptionSelection {
  plan: IAccountSubscriptionPlan;
  pricing: IAccountSubscriptionPlanPricing;
}

// Locales with a published Privacy Policy translation at logbook.yachts. Any other app
// language (e.g. fr, which has no translation yet) falls back to English.
const privacyPolicyLocales = ["en", "de"];

const privacyPolicyUrl = (language: string): string => {
  const locale = privacyPolicyLocales.includes(language) ? language : "en";
  return `https://logbook.yachts/site/${locale}/privacy`;
};

// Whole days from now until an ISO date (negative once it is in the past).
const daysUntil = (iso: string): number =>
  Math.floor((Date.parse(iso).valueOf() - Date.now()) / (24 * 60 * 60 * 1000));

// App Store guideline 3.1.2(c): the billed amount must always be shown in the app's own
// purchase flow, not only in Apple's system StoreKit sheet. If the native iapProductInfo
// fetch fails (offline, or the kind of transient StoreKit catalog issue Apple's review
// hit -- see guideline 2.1(b) in the same rejection), fall back to the flat
// Apple-configured price for the skipper plan (44.99, billed in USD for the US storefront
// and EUR for every other one) rather than silently hiding the price row while leaving
// Subscribe enabled.
//
// Keyed on the App Store *storefront*, never the device language: those disagree
// routinely (a German-language iPad on the US storefront is billed in USD), and the
// storefront is the one Apple bills against. With no storefront reported at all, EUR is
// the right default -- the US is the only storefront this product is priced in USD on.
//
// Note this is deliberately NOT derived from the server's plan pricing: that is the
// Stripe price (EUR 39.00), which is intentionally lower than the Apple one.
const iapFallbackPrice = (storefront: string | undefined): string => (storefront === "USA" ? "$44.99" : "44.99 €");

export function AccountView({ account, backAction, checkoutSessionId }: AccountViewProps) {
  const app = GetApp()!;
  const t = app.getT("AccountView");
  const [currency, setCurrency] = useState<AccountViewCurrency>(AccountViewCurrency.EUR);
  const [checkoutCompleted, setCheckoutCompleted] = useState<boolean>(false);
  const [checkoutClientSecret, setCheckoutClientSecret] = useState<string | undefined>(undefined);
  const [selected, setSelected] = useState<AccountSubscriptionSelection | undefined>(undefined);
  const [iapPrice, setIapPrice] = useState<string | undefined>(undefined);
  // The App Store storefront the device is billed against, used to pick the fallback
  // currency when StoreKit could not resolve the price itself.
  const [iapStorefront, setIapStorefront] = useState<string | undefined>(undefined);
  // Which Logbook account holds this Apple ID's subscription, when it is not this one.
  // Discovered without prompting (see the entitlement probe below) so the card can
  // explain the situation instead of offering a purchase Apple would refuse.
  const [linkedAccountName, setLinkedAccountName] = useState<string | undefined>(undefined);
  // Set while an explicit Restore Purchases is running, and afterwards to the dialog it
  // produced: a confirmation to move the subscription here, or "nothing to restore".
  const [restoring, setRestoring] = useState<boolean>(false);
  const [restoreDialog, setRestoreDialog] = useState<"claim" | "nothing" | undefined>(undefined);
  const catalogReady = checkoutSessionId === undefined || checkoutCompleted;
  const { plans, stripeKey, appAccountToken } = useAccountPlanCatalog(catalogReady);

  // Show the iOS in-app-purchase view: on in the native iOS app; in a cloud/browser
  // session it stays behind the `logbook-inapp` opt-in. Computed inline (not a
  // one-shot useMemo) because `appStyles` is populated asynchronously from the native
  // bridge after mount, so a frozen memo would capture the pre-bridge value.
  const useNonStripeView = iapPurchaseEnabled(app);

  // The skipper plan is represented on iOS by the Apple IAP card. It replaces the
  // regular skipper block (to avoid a confusing duplicate), shows Cancel when the
  // account already has an active IAP subscription, and is hidden entirely when the
  // account is on an active Stripe subscription (Stripe is managed on the web only —
  // an in-app IAP purchase must not be offered on top of a Stripe subscription).
  const skipperPlan = useMemo(() => (plans || []).find((p) => p.plan.name === "skipper"), [plans]);
  // A cancelled subscription whose paid period has ended (or an expired trial) has lost
  // access, so it must be offered re-subscription rather than a stale "access ends in
  // -N days" countdown. Mirrors the server + app access gate (subscriptionExpired).
  const isSubscriptionExpired = subscriptionExpired(account);
  const hasActiveSubscription = (account.subscriptionStatus === IAccountSubscriptionStatus.active || account.subscriptionStatus === IAccountSubscriptionStatus.cancelled) && !isSubscriptionExpired;
  const activeAppleSubscription = hasActiveSubscription && account.paymentSystem === IAccountPaymentSystem.apple;
  const activeStripeSubscription = hasActiveSubscription && account.paymentSystem === IAccountPaymentSystem.stripe;
  const activeGiftSubscription = hasActiveSubscription && account.paymentSystem === IAccountPaymentSystem.gift;
  const showIapCard = useNonStripeView && !activeStripeSubscription && !activeGiftSubscription;

  // On the IAP-only view (native iOS, or the `?inapp=true` browser) only the skipper
  // plan is available as an in-app purchase, so the non-IAP tiers (captain,
  // master_captain) and the free plan are hidden entirely: the App Store forbids
  // advertising paid content/tiers that cannot be bought via In-App Purchase.
  const visiblePlans = (plans || []).filter((plan) => !useNonStripeView || plan.plan.name === "skipper");

  // App Store guideline 3.1.2(c): the billed amount must be clearly displayed in the
  // app's own purchase flow, not only in the system StoreKit sheet shown later. Fetches
  // StoreKit's already-localized/formatted price eagerly (it's not instant -- a network
  // round-trip to the App Store) so it's ready by the time the user looks at the card;
  // failing/omitted (e.g. offline) leaves iapPrice unset and the render falls back to
  // iapFallbackPrice() -- the row is never hidden, and Subscribe still works either way.
  useEffect(() => {
    if (!showIapCard) {
      return;
    }
    Native.loadIAPProductInfo().then((info) => {
      if (!info) {
        return;
      }
      if (info.price) {
        setIapPrice(info.price);
      }
      if (info.storefront) {
        setIapStorefront(info.storefront);
      }
      // Logged so a storefront/price mismatch reported from the field can be diagnosed
      // without a debug build.
      logger.debug("loadIAPProductInfo", info);
    }).catch((err) => logger.debug("loadIAPProductInfo error", err));
  }, [showIapCard]);

  // Apple never sells the same auto-renewing product twice to one Apple ID, so if this
  // Apple ID's subscription is already linked to a different Logbook account, Subscribe
  // here can only ever land the user in a StoreKit sheet that says "you're already
  // subscribed". Probe for that on opening the card — the native side reconciles the
  // entitlements the device already holds without contacting the App Store, so this
  // never triggers a sign-in prompt — and swap the button for an explanation.
  useEffect(() => {
    if (!showIapCard || activeAppleSubscription) {
      return;
    }
    Native.loadAppStoreEntitlementState().then((result) => {
      if (result?.outcome === "linkedToAnotherAccount") {
        setLinkedAccountName(result.accountName);
      }
    }).catch((err) => logger.debug("appStoreEntitlementState error", err));
  }, [showIapCard, activeAppleSubscription]);

  useEffect(() => {
    const load = async () => {
      if (checkoutSessionId && !checkoutCompleted) {
        const response = await AccountViewData.completeCheckout(app.apiClient, checkoutSessionId);
        if (response && response.planId) {
          const planid = response.planId;
          const account = app.account;
          logger.debug("Checkout complete");
          setCheckoutCompleted(true);
          if (account) {
            app.updateSession({
              ...account,
              accountSubscriptionPlanId: planid,
              subscriptionStatus: IAccountSubscriptionStatus.active,
            });
          }
        } else {
          return;
        }
      }
    };
    load().catch(console.error);
  }, [checkoutSessionId, checkoutCompleted]);

  const selectCheckoutPlan = useCallback(async (selection: AccountSubscriptionSelection) => {
    if (!checkoutClientSecret) {
      const reponse = await AccountViewData.checkout(app.apiClient, { currency: currency, lang: app.language, planId: selection.plan.id, planPricingId: selection.pricing.id, url: window.location.href });
      logger.debug("checkout response", selection.plan.id, reponse.clientSecret != undefined);
      setCheckoutClientSecret(reponse.clientSecret);
    }
    setSelected(selection);
  }, []);

  const cancelPlan = useCallback(async () => {
    const response = await AccountViewData.cancelPlan(app.apiClient);
    if (response && response.success) {
      window.location.reload();
    }
  }, []);

  // Start the native StoreKit purchase (buy the skipper IAP product).
  const performIapPurchase = useCallback(() => {
    Native.performIAP("skipper", appAccountToken);
  }, [appAccountToken]);

  // Open the native system Manage Subscriptions sheet. Apple subscriptions can only be
  // cancelled there (not server-side); cancelling emits a DID_CHANGE_RENEWAL_STATUS
  // notification the webhook reflects on the account.
  const manageSubscriptions = useCallback(() => {
    Native.manageSubscriptions();
  }, []);

  // App Store guideline 3.1.1: users must be able to explicitly initiate a restore —
  // relying only on the automatic entitlement sync on launch is not sufficient. The
  // native side re-fetches the Apple ID's transaction history and re-posts every
  // entitlement to the server, then reports what happened.
  const applySyncResult = useCallback((result: AppStoreSyncResult | undefined) => {
    switch (result?.outcome) {
      case "activated":
      case "claimed":
        // The account changed server-side; reload so the page reflects it.
        window.location.reload();
        break;
      case "linkedToAnotherAccount":
        // The subscription belongs to another Logbook account. Offer to move it —
        // never silently, since that account loses access.
        setLinkedAccountName(result.accountName);
        setRestoreDialog("claim");
        break;
      default:
        setRestoreDialog("nothing");
        break;
    }
  }, []);

  const restorePurchases = useCallback(async () => {
    setRestoring(true);
    try {
      applySyncResult(await Native.restorePurchases());
    } finally {
      setRestoring(false);
    }
  }, [applySyncResult]);

  // The user confirmed moving the subscription to this account. Only this path may
  // transfer it; the automatic reconciliation never does.
  const claimSubscription = useCallback(async () => {
    const result = await Native.claimAppStoreSubscription();
    setRestoreDialog(undefined);
    applySyncResult(result);
  }, [applySyncResult]);

  const _selectCurrency = useCallback((currency?: string) => {
    if (currency && currency in AccountViewCurrency) {
      setCurrency(currency as AccountViewCurrency);
    }
  }, []);

  const stripePromise = useMemo(() => stripeKey ? loadStripe(stripeKey) : null, [stripeKey]);
  const [_stripeOptions, checkoutOptions, _paymentOptions, _addressOptions] = useMemo(() => {
    if (!selected) {
      return [{}, { clientSecret: checkoutClientSecret || "", elementsOptions: {} }, {}, {}];
    }
    const elements: StripeElementsOptions = {
      mode: "subscription",
      amount: selected.pricing.price,
      currency: selected.pricing.currency.toLowerCase(),
      appearance: {
        theme: app.theme == "light" ? "stripe" : "night",
      },
    };
    const checkout = {
      clientSecret: checkoutClientSecret || "",
    };
    const payment: StripePaymentElementOptions = {
      layout: "accordion",
      business: {
        name: "Trimutic UG (haftungsbeschränkt)",
      },
    };
    const address: StripeAddressElementOptions = {
      mode: "billing",
    };
    return [elements, checkout, payment, address];
  }, [selected]);

  const _price = useMemo(() => plans && selected && t(
    `price_${currency}`, {
      price: Math.floor(selected.pricing.price / 100),
      duration: t(`price_duration_${selected.pricing.duration}`),
    },
  ), [plans, selected, currency]);

  return (
    <div className="AccountView">
      <div className="AccountView-payment">
        <BlockContainer className="block-row-center">
          <h2 className="block_field_plans_600">{t(useNonStripeView ? "plans_iPad" : "plans")}</h2>
          {checkoutSessionId && (checkoutCompleted ?
              <LocalizedMessage message={(m) => m.subscription.checkoutComplete} /> :
              <LocalizedMessage message={(m) => m.subscription.checkoutProcessing} />)}
        </BlockContainer>
        {plans ? (
          <>
            {selected && !useNonStripeView ? (
              <EmbeddedCheckoutProvider stripe={stripePromise} options={checkoutOptions}>
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            ) : (
              <>
                <BlockContainer className="block-row-center block_field_plans_600">
                  <LocalizedMessage message={(m) => useNonStripeView ? m.subscription.subscriptionMessageiPad : m.subscription.subscriptionMessage}
                    className="text-bigger"
                  />
                </BlockContainer>
                <br></br>
                <BlockContainer className={`AccountView-plans${useNonStripeView ? " non-stripe-view" : ""}`}>
                  {visiblePlans.map((plan) => {
                    // On iOS the skipper plan is shown as the Apple IAP card: Subscribe, or
                    // Cancel when the account already has an active IAP subscription. It
                    // replaces the regular skipper block to avoid a duplicate, and is hidden
                    // when the account is on an active Stripe subscription (the regular block
                    // below handles Stripe management in that case).
                    if (useNonStripeView && plan.plan.name === "skipper" && showIapCard) {
                      return [
                        <Block key="apple-iap-skipper" className={classNames(["AccountView-plan", "AccountView-plan-skipper", "AccountView-plan-iap", activeAppleSubscription && "active"])}>
                          <BlockRow>
                            <h2 className="AccountView-plan-name">{t("plan_skipper")}</h2>
                          </BlockRow>
                          <BlockRow>
                            <LocalizedMessage message={(m) => m.planDescriptions.skipper_inApp} className="AccountView-plan-description" />
                          </BlockRow>

                          {skipperPlan && skipperPlan.features.filter((f) => f.amount != 0).map((f) => {
                            let feature = null;
                            switch (f.feature) {
                              case "active_ships":
                              case "active_users":
                              case "active_tenders":
                                feature = <BlockRow className="AccountView-plan-info">{t(`feature_${f.feature}`, { amount: (f.amount ?? 0) < 0 ? t("feature_unlimited") : (f.amount ?? 0) })}</BlockRow>;
                                break;
                              case "support":
                                feature = <BlockRow className="AccountView-plan-info">{t(`feature_support_${f.amount}`)}</BlockRow>;
                                break;
                              default:
                                return null;
                            }
                            return (
                              <React.Fragment key={f.feature}>
                                {feature}
                              </React.Fragment>
                            );
                          })}

                          {activeAppleSubscription ? (
                            <>
                              {account.subscriptionStatus === IAccountSubscriptionStatus.cancelled ? (
                                account.nextBillingAt && (
                                  <BlockRow>
                                    <BlockField>
                                      <p><strong>{t("cancelled_ends", { days: daysUntil(account.nextBillingAt) })}</strong></p>
                                    </BlockField>
                                  </BlockRow>
                                )
                              ) : (
                                account.nextBillingAt && (
                                  <BlockRow>
                                    <BlockField>
                                      <p><strong>{t("renews_in", { days: daysUntil(account.nextBillingAt) })}</strong></p>
                                    </BlockField>
                                  </BlockRow>
                                )
                              )}
                              <BlockRow>
                                <BlockField>
                                  <button onClick={() => manageSubscriptions()} className="button subscribe-button">
                                    {t("cancel_plan")}
                                  </button>
                                </BlockField>
                              </BlockRow>
                            </>
                          ) : (
                            <>
                              {/* Same markup/classes as AccountViewPlanBlock's Stripe price row, so
                                  the billed amount gets identical, prominent styling (guideline
                                  3.1.2(c): the billed amount must be the most clear/conspicuous
                                  pricing element -- there is no free-trial/intro pricing shown here
                                  to compete with it). Falls back to iapFallbackPrice() when the
                                  native StoreKit fetch failed, so the row is never hidden. */}
                              <BlockRow className="block-row-no-margin price-row">
                                <BlockField className="AccountView-plan-price-info">
                                  <span className="price block-row-actions">
                                    {t("price_native", { price: iapPrice || iapFallbackPrice(iapStorefront), duration: t("price_duration_30") })}
                                  </span>
                                </BlockField>
                              </BlockRow>
                              {/* IAP-only key (not the shared duration_30 also used by the
                                  Stripe PlanBlock): guideline 3.1.2(c)/Schedule 2 requires
                                  explicitly disclosing that the subscription auto-renews
                                  unless canceled, which "monthly cancelable" alone doesn't say. */}
                              <BlockRow className="AccountView-plan-price-info duration-info block-row-no-margin">
                                {t("duration_30_iap")}
                              </BlockRow>
                              <BlockRow>
                                <BlockField>
                                  {linkedAccountName ? (
                                    // Apple will not sell this Apple ID the same
                                    // subscription twice, so Subscribe could only open a
                                    // StoreKit sheet saying "you're already subscribed".
                                    // Explain where the subscription is, and point at
                                    // Restore Purchases as the way to move it here.
                                    <p className="AccountView-plan-linked-elsewhere">
                                      {t("linked_to_other_account", { account: linkedAccountName })}
                                      {" "}
                                      {t("linked_to_other_account_hint")}
                                    </p>
                                  ) : (
                                    <button onClick={() => performIapPurchase()} className="button subscribe-button">
                                      {t("subscribe_api")}
                                    </button>
                                  )}
                                </BlockField>
                              </BlockRow>
                            </>
                          )}
                        </Block>,
                        <div key="flex-break" className="flex-break"></div>,
                      ];
                    }

                    return [
                      useNonStripeView ? (
                        <AccountViewPlanBlockNonStripe
                          key={plan.plan.id}
                          active={account.accountSubscriptionPlanId == plan.plan.id && !isSubscriptionExpired}
                          cancelCallback={cancelPlan}
                          canSubscribe={account.accountSubscriptionPlanId == 1 || isSubscriptionExpired}
                          currency={currency}
                          plan={plan}
                        />
                      ) : (
                        <AccountViewPlanBlock
                          key={plan.plan.id}
                          active={account.accountSubscriptionPlanId == plan.plan.id && !isSubscriptionExpired}
                          cancelCallback={cancelPlan}
                          canSubscribe={account.accountSubscriptionPlanId == 1 || isSubscriptionExpired}
                          currency={currency}
                          plan={plan}
                          selectCallback={(pricing) => selectCheckoutPlan({ plan: plan.plan, pricing: pricing })}
                        />
                      ),
                    ];
                  },

                  )}
                </BlockContainer>
                {useNonStripeView && (
                  // App Store guideline 3.1.2: a functional link to the Terms of Use (EULA)
                  // and Privacy Policy must be shown wherever the native App Store IAP
                  // purchase flow is offered. Apple's own standard EULA is used (registered
                  // in App Store Connect), so it's linked here rather than a custom one; it
                  // has a single canonical URL with no per-language variant, unlike the
                  // Privacy Policy, which is localized to the app language.
                  <BlockContainer className="block-row-center AccountView-legal-links">
                    <p className="text-centered">
                      <a href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" target="_blank" rel="noreferrer">{t("terms_of_use")}</a>
                      {" · "}
                      <a href={privacyPolicyUrl(app.language)} target="_blank" rel="noreferrer">{t("privacy_policy")}</a>
                    </p>
                  </BlockContainer>
                )}
                {useNonStripeView && (
                  // App Store guideline 3.1.1: a distinct, user-initiated "Restore" action
                  // is required — automatically restoring on launch is explicitly called out
                  // by Apple as insufficient. Always shown on the IAP view (not just when a
                  // subscription is missing) so it's reachable regardless of account state.
                  <BlockContainer className="block-row-center">
                    <BlockRow actions={true}>
                      <BlockField>
                        <button onClick={() => { restorePurchases().catch((err) => logger.debug("restorePurchases error", err)); }} className="button" disabled={restoring}>
                          {restoring ? t("restore_purchases_running") : t("restore_purchases")}
                        </button>
                      </BlockField>
                    </BlockRow>
                  </BlockContainer>
                )}
                {restoreDialog === "claim" && (
                  // The other account loses access, so this is never done silently: the
                  // user is told exactly which account holds the subscription and must
                  // confirm before it moves.
                  <ConfirmDialog
                    awaitConfirm={true}
                    cancelLabel={t("restore_claim_cancel")}
                    confirmLabel={t("restore_claim_confirm")}
                    message={t("restore_claim_message", { account: linkedAccountName || "", current: account.name })}
                    onCancel={() => setRestoreDialog(undefined)}
                    onConfirm={claimSubscription}
                    title={t("restore_claim_title")}
                  />
                )}
                {restoreDialog === "nothing" && (
                  <InfoDialog
                    message={t("restore_nothing_message")}
                    onClose={() => setRestoreDialog(undefined)}
                    title={t("restore_nothing_title")}
                  />
                )}
              </>
            )}
          </>
        ) : <Loading />}
      </div>
      <BlockContainer className="block-row-center margin-top-large">
        <BlockRow actions={true}>
          <BlockField>
            <button className="button" onClick={backAction}>{t("cancel")}</button>
          </BlockField>
        </BlockRow>
      </BlockContainer>
    </div>
  );
}
